import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuth } from '../store';
import { getAllTemplates } from '../services/firestore';
import { isUltraAdmin } from '../utils';
import type { Event } from '../types';

/**
 * Select Template Page
 * Regular users select a template to create an event from
 */
export function SelectTemplatePage() {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated || !currentUser) {
      navigate('/');
      return;
    }
  }, [isAuthenticated, currentUser, navigate]);

  // Load all templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const loadedTemplates = await getAllTemplates();
        
        // Check if current user is ultra admin
        const isAdmin = currentUser && isUltraAdmin(currentUser);
        
        // Filter to only available AND published templates
        let availableTemplates = loadedTemplates.filter(t => 
          t.isAvailableForEvents !== false && 
          (t.published ?? true) // Treat undefined as published for backward compatibility
        );
        
        // Hide completed/finished templates from non-ultra-admins
        if (!isAdmin) {
          availableTemplates = availableTemplates.filter(t => 
            !t.finished && t.status !== 'completed'
          );
        }
        
        // Sort by template usage count (most popular first)
        availableTemplates.sort((a, b) => (b.templateUsageCount || 0) - (a.templateUsageCount || 0));
        setTemplates(availableTemplates);
      } catch (err) {
        console.error('Failed to load templates:', err);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadTemplates();
    }
  }, [currentUser]);

  const handleSelectTemplate = (templateId: string) => {
    navigate(`/create?template=${templateId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-primary-text">Create Event</h1>
              <p className="text-sm text-secondary-text">Choose a template to get started</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/games')}
            >
              Back
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {templates.length === 0 ? (
          <Card padding="lg">
            <div className="text-center py-8">
              <p className="text-secondary-text mb-4">No templates available yet</p>
              <p className="text-sm text-muted-text mb-6">
                Templates are fight cards created by administrators. Check back soon!
              </p>
              <Button
                variant="secondary"
                onClick={() => navigate('/games')}
              >
                Back to Games
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="mb-4">
              <p className="text-secondary-text text-sm">
                Select a template below. All fights and odds will be copied to your new event.
              </p>
            </div>
            {templates.map((template) => (
              <Card key={template.id} padding="md">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-primary-text">{template.name}</h3>
                    <div className="flex gap-2 items-center mt-2">
                      <Badge variant="info" size="sm">
                        Template
                      </Badge>
                      {template.templateUsageCount && template.templateUsageCount > 0 ? (
                        <span className="text-sm text-secondary-text">
                          ⭐ Used {template.templateUsageCount} time{template.templateUsageCount !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-text">
                          New template
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-tertiary-bg rounded-lg">
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Sport</div>
                    <div className="font-semibold text-primary-text">
                      {template.sport || 'UFC'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Fights</div>
                    <div className="font-semibold text-primary-text">
                      {template.fights.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-secondary-text mb-1">Created By</div>
                    <div className="font-semibold text-primary-text">
                      {template.adminUsername || 'Admin'}
                    </div>
                  </div>
                </div>

                {/* Show a preview of fights */}
                {template.fights.length > 0 && (
                  <div className="mb-4 p-3 bg-tertiary-bg rounded-lg">
                    <div className="text-xs text-secondary-text mb-2">Preview:</div>
                    <div className="space-y-1">
                      {template.fights.slice(0, 3).map((fight, idx) => (
                        <div key={idx} className="text-sm text-primary-text">
                          • {fight.fighters[0]?.name || 'Fighter 1'} vs {fight.fighters[1]?.name || 'Fighter 2'}
                        </div>
                      ))}
                      {template.fights.length > 3 && (
                        <div className="text-sm text-muted-text">
                          + {template.fights.length - 3} more fight{template.fights.length - 3 !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={() => handleSelectTemplate(template.id)}
                >
                  Use This Template
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
